import type { PageModule } from "./page-module"
import { playHeartbeat } from "./cmn-icon-hover"

/** min-max クランプ */
const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v))

/** 「一瞬止まる」ホールドが消費する生progress幅（solutionと同方式・一瞬） */
const HOLD_W = 0.07

/** 焦点から2枚以上離れた画像はさらに強くぼかす（is-far） */
const FAR_DISTANCE = 2

/** テキスト差し替えのクロスフェード用ディレイ(ms)（ぼかし→差し替え→戻し） */
const SWAP_DELAY = 90

/**
 * 「一瞬止まる」：生スクロール進行度を、指定アニメ進行度(holds)で平坦(ホールド)化した進行度へ変換。
 * ホールド帯では構図が動かず＝スクロールしても静止して見える（実スクロールは止めないのでカクつかない）。
 * solution-orbit と同じロジック。
 * @param {number} raw 生の進行度0-1
 * @param {number[]} holds 止めるアニメ進行度（昇順）
 * @returns {number} ホールド込みのアニメ進行度0-1
 */
const applyHolds = (raw: number, holds: number[]): number => {
  const k = 1 - holds.length * HOLD_W // 移動区間の raw/animP
  let rawCursor = 0
  let prevAnim = 0
  for (const h of holds) {
    const movingRaw = (h - prevAnim) * k
    if (raw < rawCursor + movingRaw) return prevAnim + (raw - rawCursor) / k
    rawCursor += movingRaw
    if (raw < rawCursor + HOLD_W) return h // ホールド中＝構図静止
    rawCursor += HOLD_W
    prevAnim = h
  }
  return prevAnim + (raw - rawCursor) / k
}

/**
 * 説明文/タイトルを要素へ安全に流し込む（<br>のみ許容・innerHTML不使用でXSS回避）。
 * 改行出し分けクラス（mq4sp/mq4pc等）は class 値だけ抽出して className で復元。
 * @param {HTMLElement} el 差し込み先
 * @param {string} html `<br>`区切りの文字列（dataの値）
 */
const setTextWithBreaks = (el: HTMLElement, html: string): void => {
  el.textContent = ""
  html.split(/(<br[^>]*>)/i).forEach((part) => {
    const br = part.match(/^<br([^>]*)>$/i)
    if (br) {
      const el2 = document.createElement("br")
      const cls = br[1].match(/class\s*=\s*["']([^"']*)["']/i)
      if (cls) el2.className = cls[1]
      el.appendChild(el2)
    } else if (part) {
      el.appendChild(document.createTextNode(part))
    }
  })
}

/**
 * SERVICE：セクションをピン留め（.service__pin）し、スクロール進行度で画像トラックを translateY。
 * 各画像がwindow中央に来る進行度で applyHolds により「一瞬止まる」（solutionと同じ体感）。
 * 中央の画像＝焦点(is-active=シャープ)、前後はぼかし。番号/英名/キャッチ/説明を止まった瞬間に差し替え。
 * jQuery・外部ライブラリ不使用。
 * @example
 * new ServiceStack();
 */
export class ServiceStack {
  root: HTMLElement | null
  body: HTMLElement | null
  pin: HTMLElement | null
  track: HTMLElement | null
  windowEl: HTMLElement | null
  images: HTMLElement[]
  currentEl: HTMLElement | null
  nameEl: HTMLElement | null
  catchEl: HTMLElement | null
  descEl: HTMLElement | null
  linkEl: HTMLAnchorElement | null
  holds: number[]
  activeIndex: number
  rafId: number | null
  io: IntersectionObserver | null
  inView: boolean
  private ac = new AbortController()

  /**
   * @param {string} selector ルート要素のセレクタ
   * @param {ParentNode} scope ルート探索の起点（既定=document）
   */
  constructor(selector = "[data-service-stack]", scope: ParentNode = document) {
    this.root = scope.querySelector(selector)
    this.body = this.root ? this.root.querySelector(".service__body") : null
    this.pin = this.root ? this.root.querySelector(".service__pin") : null
    this.track = this.root ? this.root.querySelector("[data-service-track]") : null
    this.windowEl = this.root ? this.root.querySelector(".service__images") : null
    this.images = this.track
      ? Array.from(this.track.querySelectorAll<HTMLElement>("[data-service-slide]"))
      : []
    this.currentEl = this.root ? this.root.querySelector("[data-service-current]") : null
    this.nameEl = this.root ? this.root.querySelector("[data-service-name]") : null
    this.catchEl = this.root ? this.root.querySelector("[data-service-catch]") : null
    this.descEl = this.root ? this.root.querySelector("[data-service-desc]") : null
    this.linkEl = this.root ? this.root.querySelector("[data-service-link]") : null
    this.holds = []
    this.activeIndex = 0
    this.rafId = null
    this.io = null
    this.inView = true

    if (!this.root || !this.body || !this.pin || !this.track || !this.windowEl || this.images.length === 0) return

    // 各画像がwindow中央に来る進行度でホールド（＝一枚ずつ中央で一瞬止まる）。例:5枚→[0, .25, .5, .75, 1]
    const n = this.images.length
    this.holds = this.images.map((_, i) => i / (n - 1))

    // ピン区間が画面内にある間だけ計算（背の高いセクションなので getBoundingClientRect churn を避ける）
    if ("IntersectionObserver" in window) {
      this.io = new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting
          if (this.inView) this.update()
        },
        { rootMargin: "0px" },
      )
      this.io.observe(this.body)
    }

    this.update()
    this.setupHoverSync()
    window.addEventListener("scroll", this.onScroll, { passive: true, signal: this.ac.signal })
    window.addEventListener("resize", this.onScroll, { signal: this.ac.signal })
  }

  /**
   * リスナー解除＆rAF/IO解放（swup離脱時の後始末）
   */
  unmount = () => {
    this.ac.abort()
    this.io?.disconnect()
    if (this.rafId !== null) cancelAnimationFrame(this.rafId)
  }

  /**
   * ピン進行度→ホールド→トラックtranslateY＋焦点/ぼかし/テキスト更新
   * @private
   */
  update = () => {
    if (!this.inView || !this.body || !this.pin || !this.track || !this.windowEl) return

    // ピン予算内の進行度（body上端が画面上端を通過した距離を予算でクランプ）＋ 各画像中央で一瞬ホールド
    const budget = Math.max(this.body.offsetHeight - this.pin.clientHeight, 1)
    const raw = clamp(-this.body.getBoundingClientRect().top / budget, 0, 1)
    const hp = applyHolds(raw, this.holds)

    const n = this.images.length
    const pos = hp * (n - 1) // 0→n-1（小数）：window中央に来る画像位置

    // トラックを translateY：pos番目の画像中心を window の縦中央へ。
    // カード高さがSPのラベル行数で不揃いなので、前後カードの実centerを補間（一定stepだと後半がズレる）
    const windowH = this.windowEl.clientHeight
    const i0 = Math.floor(pos)
    const i1 = Math.min(i0 + 1, n - 1)
    const centerOf = (i: number): number =>
      this.images[i].offsetTop + this.images[i].offsetHeight / 2
    const centerAtPos = centerOf(i0) + (centerOf(i1) - centerOf(i0)) * (pos - i0)
    this.track.style.transform = `translateY(${Math.round(windowH / 2 - centerAtPos)}px)`

    // 中央＝焦点(シャープ) / 離れた画像はぼかし
    const active = Math.round(pos)
    this.images.forEach((img, i) => {
      const dist = Math.abs(i - active)
      img.classList.toggle("is-active", dist === 0)
      img.classList.toggle("is-far", dist >= FAR_DISTANCE)
      if (dist !== 0) img.classList.remove("is-hover") // 非アクティブ化した画像のホバー同期状態を掃除
    })

    if (active === this.activeIndex) return
    this.activeIndex = active
    this.swapTexts(active)
  }

  /**
   * 番号・英名・和文キャッチ・説明文をクロスフェードで差し替え（止まった瞬間に確定）
   * @private
   * @param {number} idx 表示するスライドのindex
   */
  swapTexts = (idx: number) => {
    const slide = this.images[idx]
    if (!slide) return
    const targets = [this.currentEl, this.nameEl, this.catchEl, this.descEl].filter(
      (el): el is HTMLElement => el !== null,
    )
    targets.forEach((el) => el.classList.add("is-changing"))
    setTimeout(() => {
      if (this.currentEl) this.currentEl.textContent = String(idx + 1).padStart(2, "0")
      if (this.nameEl) this.nameEl.textContent = slide.dataset.name || ""
      // タイトル(キャッチ)・説明文は<br>入り。innerHTML不使用でテキストノード＋<br>要素に安全に組む
      if (this.catchEl) setTextWithBreaks(this.catchEl, slide.dataset.catch || "")
      if (this.descEl) setTextWithBreaks(this.descEl, slide.dataset.desc || "")
      if (this.linkEl && slide.dataset.url) this.linkEl.href = slide.dataset.url
      targets.forEach((el) => el.classList.remove("is-changing"))
    }, SWAP_DELAY)
  }

  /**
   * PC限定：アクティブ画像 ↔ キャッチ を「ホバーペア」として同期。
   * どちらをホバーしても 画像ズーム(.is-hover)＋テキスト1拍＋アイコンスライドが一緒に走る。
   * @private
   */
  setupHoverSync = () => {
    const isPC = () => window.matchMedia("(min-width: 1025px)").matches
    const sig = this.ac.signal

    this.images.forEach((img) => {
      img.addEventListener(
        "pointerenter",
        () => {
          if (!isPC() || !img.classList.contains("is-active")) return
          img.classList.add("is-hover")
          if (this.linkEl) playHeartbeat(this.linkEl, "p", ".Icon")
        },
        { signal: sig },
      )
      img.addEventListener("pointerleave", () => img.classList.remove("is-hover"), { signal: sig })
    })

    if (this.linkEl) {
      this.linkEl.addEventListener(
        "pointerenter",
        () => {
          if (isPC()) this.images[this.activeIndex]?.classList.add("is-hover")
        },
        { signal: sig },
      )
      this.linkEl.addEventListener(
        "pointerleave",
        () => this.images[this.activeIndex]?.classList.remove("is-hover"),
        { signal: sig },
      )
    }
  }

  /**
   * スクロール／リサイズ（rAFで間引き）
   * @private
   */
  onScroll = () => {
    if (this.rafId !== null) return
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null
      this.update()
    })
  }
}

/**
 * ServiceStack を現在ページの scope 起点で mount し、destroy を返す
 * @param {HTMLElement} scope 現在ページの <main>
 * @returns {(() => void) | void} 後始末関数（対象がなければ何もしない）
 */
export const mountServiceStack: PageModule = (scope) => {
  const inst = new ServiceStack("[data-service-stack]", scope)
  return () => inst.unmount()
}
